// ============================================================================
// PER-COMPANY APPS OVERRIDE (reseller company switch)
// ============================================================================
// When an admin reseller switches the active company from the header selector,
// the list of available apps must reflect THAT company's tenant configuration
// (`tenants/{slug}.appsConfig`), while the theme / layout / look-and-feel stay
// the logged-in reseller's. This module owns that apps-ONLY override.
//
// Decoupling from the main runtime config (which carries theme + apps + schema
// together) is what lets us swap apps without swapping theme: `getRuntimeApps()`
// prefers this override, every other getter keeps reading the reseller config.
//
// Lifecycle:
//   • At selection time (authenticated app code) we resolve the selected
//     company's tenant id and persist it here, then reload (see
//     `companyAppsSwitch.ts`). Companies WITHOUT a tenant apps config persist a
//     "missing" marker instead, so the Apps Hub can prompt to enable apps.
//   • At boot, BEFORE the app/config modules are imported, `main.tsx` calls
//     `ensureCompanyAppsOverrideReady()` which loads the override apps preset via
//     the public endpoint so `appsConfig.ts` reads the right preset.
// ============================================================================

import type { AppsPreset } from "../apps/types";
import { fetchPublicTenantConfigById } from "./tenantConfigClient";

// localStorage keys. Namespaced and SEPARATE from the auth/business-scoping keys
// (`companyId` / `userCompanyId`) and from the runtime tenant key
// (`cc:tenantConfig:companyId`, which drives theme) — do not conflate them.
const APPS_TENANT_KEY = "cc:appsOverride:tenantId";
const APPS_MISSING_KEY = "cc:appsOverride:missing";

let overrideApps: AppsPreset | null = null;
let overrideMissing = false;
let resolved = false;

function readLS(key: string): string | null {
  try {
    return typeof window !== "undefined"
      ? window.localStorage.getItem(key)
      : null;
  } catch {
    return null;
  }
}

function writeLS(key: string, value: string | null): void {
  try {
    if (typeof window === "undefined") return;
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* private mode / quota — selection still works for this session */
  }
}

// ---------------------------------------------------------------------------
// Synchronous getters (consumed by runtimeConfig / useFilteredCategories / UI)
// ---------------------------------------------------------------------------

/** The selected company's apps preset, or null to fall back to the reseller's. */
export function getCompanyAppsOverride(): AppsPreset | null {
  return overrideApps;
}

/** True when a company is selected that has NO tenant apps config. */
export function isCompanyAppsMissing(): boolean {
  return overrideMissing;
}

// ---------------------------------------------------------------------------
// Boot hydration (public fetch only — no auth, runs before app modules import)
// ---------------------------------------------------------------------------

/**
 * Load the persisted apps override before the config modules read it. Resolves
 * to a no-op when nothing is overridden. Never throws.
 */
export async function ensureCompanyAppsOverrideReady(): Promise<void> {
  if (resolved) return;

  if (readLS(APPS_MISSING_KEY)) {
    // Same stale-override guard: discard if no company is currently selected.
    if (!(readLS("companyId") || "")) {
      writeLS(APPS_MISSING_KEY, null);
      resolved = true;
      return;
    }
    overrideMissing = true;
    resolved = true;
    return;
  }

  const tenantId = readLS(APPS_TENANT_KEY);
  if (tenantId) {
    // Guard: if there is a tenant override but no active company selection in
    // localStorage ("companyId"), the override is stale — it was left behind
    // when the company was deselected without going through the full
    // handleCompanyChange flow (e.g., store reset on a fresh page load after
    // the company was programmatically cleared). Silently discard it so the
    // reseller's own apps are shown instead of a customer's limited set.
    const activeCompanyId = readLS("companyId") || "";
    if (!activeCompanyId) {
      writeLS(APPS_TENANT_KEY, null);
      writeLS(APPS_MISSING_KEY, null);
      resolved = true;
      return;
    }

    const payload = await fetchPublicTenantConfigById(tenantId);
    if (payload?.apps) {
      overrideApps = payload.apps;
    } else {
      // The tenant lost its apps config between selection and reload → prompt.
      overrideMissing = true;
    }
  }

  resolved = true;
}

// ---------------------------------------------------------------------------
// Setters (called at selection time, just before a reload)
// ---------------------------------------------------------------------------

/** Source apps from a specific tenant id on the next boot. */
export function setCompanyAppsTenant(tenantId: string): void {
  writeLS(APPS_TENANT_KEY, tenantId);
  writeLS(APPS_MISSING_KEY, null);
}

/** Mark the selected company as having no apps config (show the prompt). */
export function setCompanyAppsMissing(): void {
  writeLS(APPS_MISSING_KEY, "1");
  writeLS(APPS_TENANT_KEY, null);
}

/** Clear any override — use the logged-in reseller's own apps config. */
export function clearCompanyAppsOverride(): void {
  writeLS(APPS_TENANT_KEY, null);
  writeLS(APPS_MISSING_KEY, null);
}
